import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/store";

import TicketDetailItem from "./TicketDetailView/TicketDetailItem";
import TicketDetailAppBar from "./TicketDetailAppBar";
import PageLoader from "../../components/Common/PageLoader";
import { fetchTicketByIdThunk } from "../../modules/ticket/ticketDetail/currentTicket/currentTicketOperations";
import { fetchMessagesByTicketIdThunk } from "../../modules/ticket/ticketDetail/messages/messageListOperations";
import {
  currentTicketSelectors,
  resetCurrentTicket,
} from "../../modules/ticket/ticketDetail/currentTicket/currentTicketSlice";
import { REQUEST_STATUS } from "@/modules/constants/REQUEST_STATUS";

export const route = "/tickets/:ticketId";

function TicketDetailContainer() {
  const dispatch = useAppDispatch();
  const { ticketId } = useParams<{ ticketId: string }>();
  const ticket = useAppSelector(currentTicketSelectors.selectCurrentTicket);
  const status = useAppSelector(
    currentTicketSelectors.selectCurrentTicketStatus,
  );
  const error = useAppSelector(currentTicketSelectors.selectCurrentTicketError);

  // Track whether the ticket has successfully loaded at least once
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    if (status === REQUEST_STATUS.SUCCEEDED && !hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
    }
  }, [status]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let cancelled = false;

    const poll = async () => {
      if (!ticketId) return;

      try {
        // Only schedule the next run if both requests succeed
        // Using unwrap ensures we only proceed when the thunk fulfills
        await Promise.all([
          dispatch(fetchTicketByIdThunk(ticketId)).unwrap(),
          dispatch(fetchMessagesByTicketIdThunk(ticketId)).unwrap(),
        ]);
        if (!cancelled) {
          timeoutId = setTimeout(poll, 10000); // Poll every 10 seconds
        }
      } catch (e) {
        // Keep logging errors but avoid overlapping requests
        console.error(e);
      }
    };

    // Initial fetch + start polling chain
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      // Reset current ticket state back to idle to avoid stale status/data when leaving the page
      dispatch(resetCurrentTicket());
    }; // Cleanup on unmount or ticketId change
  }, [ticketId, dispatch]);

  const showInitialLoader =
    status === REQUEST_STATUS.IDLE ||
    (status === REQUEST_STATUS.LOADING && !hasLoadedOnce.current);

  return (
    <>
      <TicketDetailAppBar />
      {/* Content */}
      {showInitialLoader ? (
        <PageLoader />
      ) : status === REQUEST_STATUS.FAILED ? (
        <div>Error loading ticket{error ? `: ${error}` : "."}</div>
      ) : (
        <TicketDetailItem item={ticket} />
      )}
    </>
  );
}

TicketDetailContainer.route = route;

export default TicketDetailContainer;
